import { forwardRef, useImperativeHandle, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller } from 'react-hook-form';
import { CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FieldGroup } from '@/components/ui/field';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { FormInput, FormField } from '@/shared/ui';

import { cn } from '@/lib/utils';
import type { AttachmentsData } from '@/features/consignment-attachment-list';
import type { ConsignmentSummary } from '@/entities/consignments';
import { useCurrentProjectData } from '@/entities/layout';

import { useConsignmentForm, type ConsignmentFormMode, type ConsignmentFormValues } from '../model';
import { PointsOfContactCard } from './points-of-contact-card';
import { StartingPointCard } from './starting-point-card';
import { ServiceIdentificationCard } from './service-identification-card';
import { AttachmentsCard } from './attachments-card';
import { ConsignmentLinesCard } from './consignment-lines-card';
import { ConsignmentControlCard } from './consignment-control-card';

interface ConsignmentEditFormProps {
  /** Whether the form creates a new consignment or edits an existing one. Defaults to 'edit'. */
  mode?: ConsignmentFormMode;
  /** Required in edit mode; absent in create mode. */
  consignmentId?: string;
  consignmentData?: ConsignmentSummary;
  /** Best-effort initial values, e.g. prefilled from a service request in create mode. */
  prefillValues?: Partial<ConsignmentFormValues>;
  /** Names for prefilled ids the option lists may not hold, e.g. an organization beyond the first hundred. */
  prefillLabels?: Partial<Record<keyof ConsignmentFormValues, string>>;
  /** Per-field messages the server already rejected, e.g. the failing fields of an SRF import. */
  serverFieldErrors?: Partial<Record<keyof ConsignmentFormValues, string>>;
  /** Per-field text the server could not resolve; seeds the control's search query. */
  serverFieldHints?: Partial<Record<keyof ConsignmentFormValues, string>>;
  isLoading: boolean;
  isReadOnly?: boolean;
  onSave: (formData: unknown) => void;
}

export interface ConsignmentEditFormRef {
  submit: () => void;
}

export const ConsignmentEditForm = forwardRef<ConsignmentEditFormRef, ConsignmentEditFormProps>(
  (
    {
      mode = 'edit',
      consignmentId,
      consignmentData,
      prefillValues,
      prefillLabels,
      serverFieldErrors,
      serverFieldHints,
      isReadOnly = false,
      isLoading,
      onSave
    },
    ref
  ) => {
    const { t } = useTranslation();
    const siteName = useCurrentProjectData()?.site?.name ?? '';
    const attachmentsRef = useRef<AttachmentsData | null>(null);
    const isEditMode = mode === 'edit';
    const { form, onSubmit } = useConsignmentForm({
      mode,
      consignmentId,
      consignmentData,
      prefillValues,
      serverFieldErrors,
      onSave,
      getAttachments: () => attachmentsRef.current
    });

    const {
      control,
      register,
      handleSubmit,
      formState: { errors }
    } = form;

    useImperativeHandle(ref, () => ({
      submit: () => {
        handleSubmit(onSubmit)();
      }
    }));

    return (
      <form onSubmit={handleSubmit(onSubmit)} className="relative space-y-6">
        {isLoading && (
          <div className="absolute w-full h-full inset-0 bg-background/50 backdrop-blur-sm flex items-center justify-center z-10">
            <Spinner className="h-8 w-8" />
          </div>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:col-span-2 gap-6">
            {/* Consignment Identification */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-medium text-muted-foreground">{t('request.id.desc')}</CardTitle>
              </CardHeader>
              <CardContent>
                <FieldGroup>
                  {/* Server-assigned identifiers only exist once the consignment is created */}
                  {isEditMode && (
                    <FormField label={t('request.cmrNumber.label')} name="consignmentId">
                      <Input id="cmrNumber" value={consignmentData?.cmrNumber || 'N/A'} disabled />
                    </FormField>
                  )}

                  <FormField label={t('requestProjectSite.site.label')} name="locationGroup">
                    <Input value={siteName} disabled />
                  </FormField>

                  {isEditMode && (
                    <>
                      <FormField label={t('request.createdByLogon.label')} name="createdBy">
                        <Input value={consignmentData?.createdByLogon || 'N/A'} disabled />
                      </FormField>

                      <FormField label={t('request.trackIdnf.label')} name="trackingCode">
                        <Input value={consignmentData?.trackIdnf || 'N/A'} disabled />
                      </FormField>
                    </>
                  )}

                  <FormInput
                    label={t('request.ownersRef.label')}
                    id="ownersReference"
                    registration={register('ownersReference')}
                    placeholder={t('request.ownersRef.desc')}
                    disabled={isReadOnly}
                    error={errors.ownersReference?.message}
                  />
                </FieldGroup>
              </CardContent>
            </Card>

            <ConsignmentControlCard
              form={form}
              consignmentId={consignmentId}
              consignment={consignmentData}
              isReadOnly={isReadOnly}
            />
            <StartingPointCard form={form} consignmentData={consignmentData} isReadOnly={isReadOnly} />
            <ServiceIdentificationCard form={form} consignmentData={consignmentData} isReadOnly={isReadOnly} />
          </div>

          <div className="flex flex-col gap-6">
            {/* Points of Contact */}
            <PointsOfContactCard
              control={control}
              watch={form.watch}
              setValue={form.setValue}
              consignmentData={consignmentData}
              prefillLabels={prefillLabels}
              serverFieldHints={serverFieldHints}
              isReadOnly={isReadOnly}
            />

            {/* Planned Movement */}
            <Card className="flex-1">
              <CardHeader>
                <CardTitle className="text-sm font-medium text-muted-foreground">{t('newcmr.movement.box')}</CardTitle>
              </CardHeader>
              <CardContent>
                <FieldGroup>
                  <FormField label={t('request.onSiteDate.label')} name="onSiteDate" error={errors.onSiteDate?.message}>
                    <Controller
                      name="onSiteDate"
                      control={control}
                      render={({ field }) => (
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              id="onSiteDate"
                              variant="outline"
                              className={cn(
                                'w-full justify-start text-left font-normal',
                                !field.value && 'text-muted-foreground'
                              )}
                              disabled={isReadOnly}>
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {field.value ? format(new Date(field.value), 'PPP') : t('request.onSiteDate.desc')}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0">
                            <Calendar
                              mode="single"
                              selected={field.value ? new Date(field.value) : undefined}
                              onSelect={(date) => field.onChange(date?.toISOString())}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      )}
                    />
                  </FormField>

                  <FormField
                    label={t('request.transportMode.label')}
                    name="plannedMode"
                    error={errors.plannedMode?.message}>
                    <Controller
                      name="plannedMode"
                      control={control}
                      defaultValue="none"
                      render={({ field }) => (
                        <Select value={field.value || 'none'} onValueChange={field.onChange} disabled={isReadOnly}>
                          <SelectTrigger id="plannedMode">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">{t('gen.none.label')}</SelectItem>
                            <SelectItem value="air">{t('transportMode.A')}</SelectItem>
                            <SelectItem value="road">{t('vehicleCategory.3')}</SelectItem>
                            <SelectItem value="sea">{t('vehicleCategory.6')}</SelectItem>
                            <SelectItem value="rail">{t('vehicleCategory.4')}</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </FormField>

                  <FormInput
                    label={t('request.missionNumber.label')}
                    id="missionTransporterNumber"
                    type="text"
                    registration={register('missionTransporterNumber')}
                    placeholder={t('request.missionNumber.desc')}
                    disabled={isReadOnly}
                    error={errors.missionTransporterNumber?.message}
                  />
                </FieldGroup>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* SLA/Invoice/Packing List Links */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground">{t('newcmr.cmr.links')}</CardTitle>
          </CardHeader>
          <CardContent>
            <FormInput
              label={t('newcmr.cmr.links')}
              id="cmrLinks"
              type="text"
              registration={register('cmrLinks')}
              placeholder={t('newcmr.cmr.links')}
              disabled={isReadOnly}
              error={errors.cmrLinks?.message}
            />
          </CardContent>
        </Card>

        {/* Attachments — listing is fetched by consignment id, so create mode has nothing to show */}
        {isEditMode && consignmentId && (
          <AttachmentsCard
            consignmentId={consignmentId}
            isReadOnly={isReadOnly}
            onAttachmentsChange={(attachments) => {
              attachmentsRef.current = attachments;
            }}
          />
        )}

        {/* Consignment Lines — only available once the consignment exists */}
        {isEditMode && consignmentId && (
          <ConsignmentLinesCard
            consignmentId={consignmentId}
            consignmentStatus={consignmentData?.statusCode}
            isReadOnly={isReadOnly}
          />
        )}
      </form>
    );
  }
);
